import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiRequest, getApiUrl } from '../config/api';
import TickEntrega from './TickEntrega';
import MediaBubble from './dashboard/MediaBubble';
import { aplicarEstadoEntrega } from '../utils/estadoEntrega';
import { AVISO_VENTANA_CERRADA, cierreVentanaWhatsApp } from '../utils/ventanaWhatsApp';
import { CODIGOS, codigoDelError, motivoDelEnvio, textoDelError } from '../utils/envioMedia';
import useVentanaCerrada from '../hooks/useVentanaCerrada';
import './SupportInbox.css';

/**
 * 🎧 Atención al cliente — bandeja de entrada del agente.
 *
 * Bandeja y mensajes paginados (mismo contrato PagedResponse que el chat en vivo):
 * - Lista: scroll infinito hacia abajo, orden por último mensaje (backend).
 * - Mensajes: page 0 = recientes; scroll arriba carga páginas antiguas.
 *
 * Multi-tenant: recibe el `botId` del tenant seleccionado en la cabecera. La bandeja se
 * acota a ese tenant y se resetea al cambiar de tenant, de modo que el agente solo ve las
 * conversaciones del bot/tenant activo.
 */
const INBOX_POLL_MS = 8000;
const MESSAGES_POLL_MS = 4000;
const INBOX_PAGE_SIZE = 30;
const MSG_PAGE_SIZE = 50;

function sendEndpoint(platform, chatId) {
  const id = encodeURIComponent(chatId);
  switch (platform) {
    case 'telegram':
      return `/telegram/chats/${id}/send`;
    case 'instagram':
      return `/instagram/chats/${id}/messages`;
    case 'whatsapp':
      return `/whatsapp/chats/${id}/messages`;
    default:
      return null;
  }
}

function formatTime(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    return d.toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch (_) {
    return '';
  }
}

function messageKey(m) {
  return m.dbId ?? m.messageId;
}

function messageTime(m) {
  const t = m.timestamp || m.createdAt;
  if (!t) return 0;
  const ms = new Date(t).getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

// Igualdad superficial de dos objetos de la API: mismos campos con los mismos
// valores. Un campo no primitivo distinto por identidad cuenta como cambio.
function mismosCampos(a, b) {
  const claves = Object.keys(a);
  if (claves.length !== Object.keys(b).length) return false;
  return claves.every(k => Object.is(a[k], b[k]));
}

/**
 * Funde una lista nueva con la anterior conservando identidades: cada elemento
 * igual al que ya había (misma clave y mismos campos) se queda con el objeto
 * viejo, y si no cambia ninguno ni el orden se devuelve el array anterior. Así
 * los sondeos de mensajes (4 s) y de bandeja (8 s) no repintan la conversación
 * entera cuando no hay nada nuevo.
 */
export function conservarIdentidad(prev, next, claveDe) {
  const porClave = new Map(prev.map(x => [claveDe(x), x]));
  let cambiado = prev.length !== next.length;
  const fundidos = next.map((x, i) => {
    const viejo = porClave.get(claveDe(x));
    const elegido = viejo && mismosCampos(viejo, x) ? viejo : x;
    if (elegido !== prev[i]) cambiado = true;
    return elegido;
  });
  return cambiado ? fundidos : prev;
}

const sessionKey = (s) => s.sessionId;

export default function SupportInbox({ botId = null }) {
  const [sessions, setSessions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingInbox, setLoadingInbox] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState(null);

  const [inboxPagination, setInboxPagination] = useState({
    currentPage: 0,
    hasMore: false,
    isLoading: false,
    totalElements: 0,
  });
  const [messagesPagination, setMessagesPagination] = useState({
    currentPage: 0,
    hasMore: false,
    isLoading: false,
  });

  const selectedRef = useRef(null);
  const sessionsRef = useRef([]);
  const messagesContainerRef = useRef(null);
  const sessionsListRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const previousScrollHeightRef = useRef(0);
  const inboxPaginationRef = useRef(inboxPagination);
  const messagesPaginationRef = useRef(messagesPagination);
  // Tenant activo. Se usa para descartar respuestas en vuelo del tenant anterior
  // (una petición disparada antes del cambio no debe pintar datos del bot viejo).
  const botIdRef = useRef(botId);

  useEffect(() => { botIdRef.current = botId; }, [botId]);
  useEffect(() => { selectedRef.current = selected; }, [selected]);
  useEffect(() => { sessionsRef.current = sessions; }, [sessions]);
  useEffect(() => { inboxPaginationRef.current = inboxPagination; }, [inboxPagination]);
  useEffect(() => { messagesPaginationRef.current = messagesPagination; }, [messagesPagination]);

  const selectedSession = sessions.find(s => s.sessionId === selected) || null;
  // Ventana de 24 h de WhatsApp: el sondeo de mensajes trae entrantes más nuevos
  // que el `lastIncomingMessageTime` de la bandeja, que se refresca más despacio.
  // El hook programa un render para el instante de cierre con la sesión abierta.
  const cierreVentana = useMemo(() => cierreVentanaWhatsApp(
    selectedSession,
    messages.filter(m => !m.isFromBot).map(m => m.timestamp || m.createdAt)
  ), [selectedSession, messages]);
  const ventanaCerrada = useVentanaCerrada(cierreVentana, selected);

  const applyUnreadZero = useCallback((list) => {
    const openId = selectedRef.current;
    if (!openId) return list;
    return list.map(s => (s.sessionId === openId ? { ...s, unreadCount: 0 } : s));
  }, []);

  // ----- Bandeja: página 0 (poll / refresh) o append (scroll) -----
  const loadInboxPage = useCallback(async (page, { append = false } = {}) => {
    if (append && inboxPaginationRef.current.isLoading) return;
    if (append) {
      setInboxPagination(p => ({ ...p, isLoading: true }));
    }
    // Tenant para el que se dispara ESTA petición. Si al resolver ya no es el
    // tenant activo, descartamos la respuesta para no mezclar conversaciones.
    const requestBotId = botId;
    try {
      // Multi-tenant: acotar la bandeja al bot/tenant seleccionado.
      const botParam = botId != null ? `&botId=${botId}` : '';
      const res = await apiRequest(
        getApiUrl(`/support/inbox?page=${page}&size=${INBOX_PAGE_SIZE}${botParam}`)
      );
      if (!res.ok) return;
      const data = await res.json();
      // Descartar respuestas obsoletas del tenant anterior (carrera al cambiar de bot).
      if (botIdRef.current !== requestBotId) return;
      const list = applyUnreadZero(Array.isArray(data?.content) ? data.content : []);

      setSessions(prev => {
        if (!append) {
          const page0Ids = new Set(list.map(s => s.sessionId));
          const rest = prev.filter(s => !page0Ids.has(s.sessionId));
          return conservarIdentidad(prev, [...list, ...rest], sessionKey);
        }
        const existing = new Set(prev.map(s => s.sessionId));
        return [...prev, ...list.filter(s => !existing.has(s.sessionId))];
      });

      const siguiente = {
        currentPage: data.page ?? page,
        hasMore: Boolean(data.hasNext),
        isLoading: false,
        totalElements: data.totalElements ?? list.length,
      };
      setInboxPagination(p => (mismosCampos(p, siguiente) ? p : siguiente));
    } catch (_) {
      // silencioso
      if (append && botIdRef.current === requestBotId) {
        setInboxPagination(p => ({ ...p, isLoading: false }));
      }
    } finally {
      // Solo apagar el "cargando" si la respuesta corresponde al tenant activo.
      if (botIdRef.current === requestBotId) {
        setLoadingInbox(false);
      }
    }
  }, [applyUnreadZero, botId]);

  const loadMoreSessions = useCallback(() => {
    const p = inboxPaginationRef.current;
    if (!p.hasMore || p.isLoading) return;
    loadInboxPage(p.currentPage + 1, { append: true });
  }, [loadInboxPage]);

  // ----- Marcar leídos -----
  const markRead = useCallback(async (sessionId) => {
    if (!sessionId) return;
    try {
      await apiRequest(getApiUrl(`/support/sessions/${sessionId}/mark-read`), { method: 'POST' });
      setSessions(prev => prev.map(s =>
        s.sessionId === sessionId ? { ...s, unreadCount: 0 } : s
      ));
    } catch (_) {
      // silencioso
    }
  }, []);

  // ----- Mensajes: página concreta -----
  const fetchMessagesPage = useCallback(async (sessionId, page) => {
    const res = await apiRequest(
      getApiUrl(`/support/sessions/${sessionId}/messages?page=${page}&size=${MSG_PAGE_SIZE}`)
    );
    if (!res.ok) return null;
    return res.json();
  }, []);

  /**
   * Refresco de los más recientes (page 0): merge por id, orden cronológico.
   * @param force si true, marca como leído aunque no haya no leídos (al ABRIR la
   * conversación). En el sondeo periódico se pasa false y solo se llama a
   * mark-read cuando de verdad hay no leídos, evitando escrituras cada 4s.
   */
  const refreshLatestMessages = useCallback(async (sessionId, { force = false } = {}) => {
    if (!sessionId) return;
    try {
      const data = await fetchMessagesPage(sessionId, 0);
      if (!data) return;
      const newestAsc = (data.content || []).slice().reverse();

      setMessages(prev => {
        if (prev.length === 0 || messagesPaginationRef.current.currentPage === 0) {
          return conservarIdentidad(prev, newestAsc, messageKey);
        }
        const map = new Map();
        for (const m of prev) map.set(messageKey(m), m);
        for (const m of newestAsc) map.set(messageKey(m), m);
        return conservarIdentidad(prev, Array.from(map.values()).sort((a, b) => {
          const dt = messageTime(a) - messageTime(b);
          if (dt !== 0) return dt;
          return (a.dbId || 0) - (b.dbId || 0);
        }), messageKey);
      });

      setMessagesPagination(p => {
        // hasMore se mantiene si ya cargamos páginas antiguas; si solo hay page 0, usar hasNext
        const hasMore = p.currentPage > 0 ? p.hasMore : Boolean(data.hasNext);
        // Sin cambios se devuelve el mismo objeto: el sondeo no debe repintar.
        return hasMore === p.hasMore && !p.isLoading ? p : { ...p, hasMore, isLoading: false };
      });

      // Solo llamamos a mark-read al abrir (force) o si hay algo por leer. Antes se
      // enviaba un POST /mark-read en cada refresco (cada 4s por sesión abierta),
      // generando escrituras innecesarias en BD.
      const session = sessionsRef.current.find(s => s.sessionId === sessionId);
      if (force || (session && (session.unreadCount || 0) > 0)) {
        await markRead(sessionId);
      }
    } catch (_) {
      // silencioso
    }
  }, [fetchMessagesPage, markRead]);

  /** Carga página antigua y la prepende (scroll infinito hacia arriba). */
  const loadMoreMessages = useCallback(async () => {
    const sessionId = selectedRef.current;
    const p = messagesPaginationRef.current;
    if (!sessionId || !p.hasMore || p.isLoading) return;

    const container = messagesContainerRef.current;
    if (container) {
      previousScrollHeightRef.current = container.scrollHeight;
    }

    setMessagesPagination(prev => ({ ...prev, isLoading: true }));
    try {
      const nextPage = p.currentPage + 1;
      const data = await fetchMessagesPage(sessionId, nextPage);
      if (!data) {
        setMessagesPagination(prev => ({ ...prev, isLoading: false }));
        return;
      }
      const olderAsc = (data.content || []).slice().reverse();
      setMessages(prev => {
        const existing = new Set(prev.map(messageKey));
        const unique = olderAsc.filter(m => !existing.has(messageKey(m)));
        return [...unique, ...prev];
      });
      setMessagesPagination({
        currentPage: data.page ?? nextPage,
        hasMore: Boolean(data.hasNext),
        isLoading: false,
      });

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = messagesContainerRef.current;
          if (el && previousScrollHeightRef.current > 0) {
            el.scrollTop = el.scrollHeight - previousScrollHeightRef.current;
          }
        });
      });
    } catch (_) {
      setMessagesPagination(prev => ({ ...prev, isLoading: false }));
    }
  }, [fetchMessagesPage]);

  // ----- Reset al cambiar de tenant (bot) -----
  // Limpiamos bandeja, selección y mensajes para no mezclar conversaciones de
  // distintos tenants. El sondeo (dep loadInboxPage, que cambia con botId) recarga
  // la página 0 del tenant nuevo justo después.
  useEffect(() => {
    setSessions([]);
    setSelected(null);
    setMessages([]);
    setInboxPagination({ currentPage: 0, hasMore: false, isLoading: false, totalElements: 0 });
    setMessagesPagination({ currentPage: 0, hasMore: false, isLoading: false });
    setLoadingInbox(true);
  }, [botId]);

  // ----- Ticks de estado en vivo -----
  // El Dashboard reenvía por el bus de ventana los eventos de .message-status.
  // El sondeo de mensajes ya trae el estado por REST; esto lo adelanta.
  useEffect(() => {
    const onEstado = (e) => {
      const estado = e.detail;
      if (!estado) return;
      if (botId != null && estado.botId != null && Number(estado.botId) !== Number(botId)) return;
      setMessages(prev => aplicarEstadoEntrega(prev, estado));
    };
    window.addEventListener('message-status', onEstado);
    return () => window.removeEventListener('message-status', onEstado);
  }, [botId]);

  // ----- Sondeo bandeja -----
  useEffect(() => {
    loadInboxPage(0);
    const t = setInterval(() => loadInboxPage(0), INBOX_POLL_MS);
    return () => clearInterval(t);
  }, [loadInboxPage]);

  // ----- Scroll infinito bandeja -----
  useEffect(() => {
    const container = sessionsListRef.current;
    if (!container) return;
    const onScroll = () => {
      if (container.scrollHeight - container.scrollTop - container.clientHeight < 100) {
        loadMoreSessions();
      }
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, [loadMoreSessions, loadingInbox, sessions.length]);

  // ----- Abrir sesión / sondeo mensajes -----
  useEffect(() => {
    if (!selected) {
      setMessages([]);
      setMessagesPagination({ currentPage: 0, hasMore: false, isLoading: false });
      return;
    }
    stickToBottomRef.current = true;
    setMessages([]);
    setMessagesPagination({ currentPage: 0, hasMore: false, isLoading: false });
    refreshLatestMessages(selected, { force: true }); // al abrir: marca leído siempre
    const t = setInterval(() => refreshLatestMessages(selectedRef.current), MESSAGES_POLL_MS);
    return () => clearInterval(t);
  }, [selected, refreshLatestMessages]);

  // ----- Scroll mensajes: stick-to-bottom + load older -----
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || !selected) return;

    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      stickToBottomRef.current = scrollHeight - scrollTop - clientHeight < 80;
      if (scrollTop < 300 && messagesPaginationRef.current.hasMore && !messagesPaginationRef.current.isLoading) {
        loadMoreMessages();
      }
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, [selected, loadMoreMessages]);

  // Una imagen que termina de cargar crece y deja la vista corta: si el agente
  // seguía abajo se vuelve a bajar; si había subido a leer, no se le mueve.
  const alCargarMedia = useCallback(() => {
    const el = messagesContainerRef.current;
    if (el && stickToBottomRef.current) el.scrollTop = el.scrollHeight;
  }, []);

  // Autoscroll solo si el usuario está abajo
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el || !stickToBottomRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Sesión cerrada / fuera de bandeja
  useEffect(() => {
    if (selected && sessions.length > 0 && !sessions.some(s => s.sessionId === selected)) {
      setSelected(null);
      setMessages([]);
    }
  }, [sessions, selected]);

  // Historial: al abrir conversación, pushState para que el atrás del SO vuelva a la lista.
  useEffect(() => {
    if (!selected) return;
    const st = window.history.state;
    if (!st || st.view !== 'support' || st.sessionId !== selected) {
      window.history.pushState({ view: 'support', sessionId: selected }, '', window.location.pathname);
    }
  }, [selected]);

  // App.js despacha esto en popstate cuando hay .has-selection
  useEffect(() => {
    const onBack = () => {
      setSelected(null);
      setMessages([]);
    };
    window.addEventListener('support-inbox-back', onBack);
    return () => window.removeEventListener('support-inbox-back', onBack);
  }, []);

  // En móvil, mismo lock de scroll/body que el chat en vivo
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    if (isMobile && selected) {
      document.body.classList.add('chat-selected');
      return () => document.body.classList.remove('chat-selected');
    }
    document.body.classList.remove('chat-selected');
  }, [selected]);

  const clearSelection = useCallback(() => {
    if (window.history.state?.view === 'support') {
      window.history.back();
      return;
    }
    setSelected(null);
    setMessages([]);
  }, []);

  const handleSelectSession = (sessionId) => {
    setSelected(sessionId);
  };

  const handleSend = async () => {
    const text = reply.trim();
    if (!text || !selectedSession || sending || ventanaCerrada) return;
    const ep = sendEndpoint(selectedSession.platform, selectedSession.chatId);
    if (!ep) { setError('Canal no soportado para el envío'); return; }
    setSending(true);
    setError(null);
    try {
      const res = await apiRequest(getApiUrl(ep), {
        method: 'POST',
        body: JSON.stringify({ text, botId: selectedSession.botId }),
      });
      if (res.status === 409) {
        // Un 409 no siempre es la ventana de 24 h: el code lo distingue.
        const data = await res.json().catch(() => null);
        setError(codigoDelError(data) === CODIGOS.VENTANA
          ? (textoDelError(data) ?? AVISO_VENTANA_CERRADA)
          : motivoDelEnvio(409, data));
        return;
      }
      if (!res.ok) {
        setError('No se pudo enviar el mensaje');
        return;
      }
      setReply('');
      stickToBottomRef.current = true;
      await refreshLatestMessages(selectedSession.sessionId);
    } catch (_) {
      setError('Error de red al enviar');
    } finally {
      setSending(false);
    }
  };

  const handleClose = async () => {
    if (!selectedSession || closing) return;
    setClosing(true);
    setError(null);
    try {
      const res = await apiRequest(
        getApiUrl(`/support/sessions/${selectedSession.sessionId}/close`),
        { method: 'POST' }
      );
      if (!res.ok && res.status !== 204) {
        setError('No se pudo cerrar la conversación');
        return;
      }
      const closedId = selectedSession.sessionId;
      // Quitar la sesión cerrada de la bandeja al instante. El refresh de página 0
      // no la elimina por sí solo: su merge conserva sesiones previas ausentes de la
      // página 0 (pensado para el scroll infinito), así que la borramos aquí.
      setSessions(prev => prev.filter(s => s.sessionId !== closedId));
      setSelected(null);
      setMessages([]);
      if (window.history.state?.view === 'support') {
        window.history.replaceState({ view: 'atencion' }, '', window.location.pathname);
      }
      await loadInboxPage(0);
    } catch (_) {
      setError('Error de red al cerrar');
    } finally {
      setClosing(false);
    }
  };

  const totalLabel = inboxPagination.totalElements || sessions.length;

  return (
    <div className={`support-inbox ${selected ? 'has-selection' : ''}`}>
      <aside className="support-list">
        <div className="support-list-header">
          <h3>Atención al cliente</h3>
          <span className="support-count">{totalLabel}</span>
        </div>
        {loadingInbox ? (
          <div className="support-empty">Cargando bandeja…</div>
        ) : sessions.length === 0 ? (
          <div className="support-empty">
            No tienes conversaciones activas. Captura una desde el Chat en vivo.
          </div>
        ) : (
          <ul className="support-sessions" ref={sessionsListRef}>
            {sessions.map(s => (
              <li
                key={s.sessionId}
                className={`support-session-item ${selected === s.sessionId ? 'active' : ''}`}
                onClick={() => handleSelectSession(s.sessionId)}
              >
                <div className={`support-avatar ${s.platform}`}>
                  <img
                    src={s.platform === 'telegram' ? '/tg.png' : s.platform === 'instagram' ? '/ig.png' : '/wa.svg'}
                    alt={s.platform}
                  />
                </div>
                <div className="support-session-body">
                  <div className="support-session-top">
                    <span className="support-session-name">{s.name}</span>
                    {s.unreadCount > 0 && <span className="support-unread">{s.unreadCount}</span>}
                  </div>
                  <div className="support-session-last">{s.lastMessage || 'Sin mensajes aún'}</div>
                  <div className="support-session-meta">Capturada: {formatTime(s.capturedAt)}</div>
                </div>
              </li>
            ))}
            {inboxPagination.isLoading && (
              <li className="support-empty">Cargando más…</li>
            )}
          </ul>
        )}
      </aside>

      <section className={`support-conversation ${selectedSession ? 'show' : ''}`}>
        {!selectedSession ? (
          <div className="support-placeholder">
            <h2>Atención al cliente</h2>
            <p>Selecciona una conversación de tu bandeja para atenderla.</p>
          </div>
        ) : (
          <>
            <header className="support-conv-header">
              <div className="support-conv-info">
                <button
                  type="button"
                  className="support-back-btn"
                  onClick={clearSelection}
                  aria-label="Volver a la bandeja"
                >
                  <svg className="back-arrow-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className="back-text">Volver</span>
                </button>
                <div className={`support-avatar ${selectedSession.platform}`}>
                  <img
                    src={selectedSession.platform === 'telegram' ? '/tg.png' : selectedSession.platform === 'instagram' ? '/ig.png' : '/wa.svg'}
                    alt={selectedSession.platform}
                  />
                </div>
                <div>
                  <h3>{selectedSession.name}</h3>
                  <span className="support-conv-sub">Atendiendo desde {formatTime(selectedSession.capturedAt)}</span>
                </div>
              </div>
              <button className="support-close-btn" onClick={handleClose} disabled={closing}>
                {closing ? 'Cerrando…' : 'Cerrar conversación'}
              </button>
            </header>

            {error && <div className="support-error">{error}</div>}

            <div className="support-messages" ref={messagesContainerRef}>
              {messagesPagination.isLoading && (
                <div className="support-empty">Cargando mensajes anteriores…</div>
              )}
              {messages.length === 0 && !messagesPagination.isLoading ? (
                <div className="support-empty">Aún no hay mensajes en esta conversación.</div>
              ) : (
                messages.map(m => (
                  <div key={messageKey(m)} className={`support-msg ${m.isFromBot ? 'agent' : 'client'}`}>
                    <div className="support-msg-bubble">
                      <p>{m.text}</p>
                      <MediaBubble msg={m} onCargado={alCargarMedia} />
                      <span className="support-msg-time">
                        {formatTime(m.timestamp || m.createdAt)}
                        {m.isFromBot && (
                          <TickEntrega
                            estado={m.deliveryStatus}
                            errorCode={m.deliveryErrorCode}
                            errorMessage={m.deliveryErrorMessage}
                          />
                        )}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>

            {ventanaCerrada && (
              <div className="support-window-warning" role="alert">{AVISO_VENTANA_CERRADA}</div>
            )}

            <div className="support-reply">
              <input
                type="text"
                placeholder="Escribe una respuesta…"
                value={reply}
                onChange={e => setReply(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSend(); }}
                disabled={sending || ventanaCerrada}
              />
              <button onClick={handleSend} disabled={sending || ventanaCerrada || !reply.trim()}>
                {sending ? 'Enviando…' : 'Enviar'}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
